import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { SourceEntity } from '../../../source/source-entity';
import { Timestamp, Timezone, TypeGuard } from '@principle-theorem/shared';
import { flow, isNull, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { PatientSourceEntity } from './patient';
import { convertExactId, getExactSourceDate } from '../../util/helpers';

export const PATIENT_BPE_RESOURCE_TYPE = 'patientBpeScore';

export const PATIENT_BPE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient BPE Scores List',
    description: '',
    idPrefix: PATIENT_BPE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IExactBPEScore {
  id: string;
  patient_id: string;
  date: string;
  user_code: string | null;
  notes: string | null;
  scores: string;
  bleedings: string;
}

export interface IExactBPEScoreTranslations {
  date: Timestamp;
}

export interface IExactBPEScoreFilters {
  patientId: string;
}

function isExactBPEScore(
  data: unknown,
  logError: boolean
): data is IExactBPEScore {
  return TypeGuard.interface<IExactBPEScore>(
    {
      id: isString,
      patient_id: isString,
      date: isString,
      user_code: [isString, isNull],
      notes: [isString, isNull],
      scores: isString,
      bleedings: isString,
    },
    logError
  )(data);
}

const BPE_SOURCE_QUERY = `
SELECT
  sourceid AS id,
  patientid AS patient_id,
  date,
  NULLIF(usercode, '') AS user_code,
  NULLIF(notes, '') AS notes,
  scores,
  bleedings
FROM
  convbpescore
WHERE
  patientid != ''
`;

export type ExactBPEScore = ISourceEntityTypes<
  IExactBPEScore,
  IExactBPEScoreTranslations,
  IExactBPEScoreFilters
>;

export class PatientBPESourceEntity extends BaseSourceEntity<ExactBPEScore> {
  sourceEntity = PATIENT_BPE_SOURCE_ENTITY;
  entityResourceType = PATIENT_BPE_RESOURCE_TYPE;
  sourceQuery = BPE_SOURCE_QUERY;
  verifySourceFn = isExactBPEScore;
  override defaultOffsetSize = 50000;
  allowOffsetJobs = true;

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([transformBPEResults]);

  getSourceRecordId(data: IExactBPEScore): string {
    return data.id;
  }

  getSourceLabel(data: IExactBPEScore): string {
    return `${data.patient_id}-${data.id}`;
  }

  translate(
    data: IExactBPEScore,
    timezone: Timezone
  ): IExactBPEScoreTranslations {
    return {
      date: getExactSourceDate(data.date, timezone),
    };
  }

  getFilterData(
    data: IExactBPEScore,
    _timezone: Timezone
  ): IExactBPEScoreFilters {
    return {
      patientId: data.patient_id,
    };
  }
}

function transformBPEResults(rows: IExactBPEScore[]): IExactBPEScore[] {
  return rows.map((row) => ({
    ...row,
    id: convertExactId(row.id),
    patient_id: convertExactId(row.patient_id),
  }));
}
